/* Thin client to transcribe-kit's /v2/transcribe endpoint.
 *
 * Two transport modes, decided at request time from the runtime config:
 *
 *   Direct mode  (config.transcribeUrl && config.apiKey)
 *     POST {transcribeUrl}/v2/transcribe with X-Api-Key header.
 *     CORS preflight is required; the upstream's stt.yaml must allow
 *     the page origin under server.cors_origins.
 *
 *   Proxy mode   (neither field present)
 *     POST /api/transcribe (same origin). Upstream relative path; the
 *     reverse proxy in front of the bundle adds X-Api-Key. No CORS,
 *     no secret in the bundle.
 */
import { getConfig } from "./config";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** A single provider attempt as recorded by transcribe-kit. */
export interface ChainAttempt {
  adapter_name: string;
  adapter_kind: string;
  outcome: string;
  latency_ms: number;
  error: string | null;
}

/** Result of POST /v2/transcribe (rich endpoint, output_format default). */
export interface TranscribeResult {
  text: string;
  raw_text: string;
  language: string | null;
  translated_text: string | null;
  translated_to: string | null;
  metrics: {
    stt_ms: number;
    llm_ms: number;
    audio_ms: number;
    upload_ms: number;
    preprocess_ms: number;
    postprocess_ms: number;
    total_ms: number;
    provider_used: string;
    provider_kind: string;
    fallback_used: boolean;
    model_used: string | null;
  };
  chain_attempts: ChainAttempt[];
}

export async function apiTranscribe(
  blob: Blob,
  filename: string,
): Promise<TranscribeResult> {
  const cfg = getConfig();
  const direct = cfg.transcribeUrl != null && cfg.apiKey != null;

  const url = direct
    ? `${cfg.transcribeUrl!.replace(/\/$/, "")}/v2/transcribe`
    : "/api/transcribe";

  const headers: Record<string, string> = {};
  if (direct) headers["X-Api-Key"] = cfg.apiKey!;

  const fd = new FormData();
  fd.append("file", blob, filename);
  if (cfg.language) fd.append("language", cfg.language);

  const r = await fetch(url, { method: "POST", headers, body: fd });

  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (body?.detail) {
        detail = typeof body.detail === "string"
          ? body.detail
          : JSON.stringify(body.detail);
      }
    } catch {
      /* response body wasn't JSON; keep the HTTP status */
    }
    throw new ApiError(r.status, detail);
  }

  return r.json();
}
